/*
 * WaWiMonitoringApp.tsx
 *
 * Created by Dr. Maximillian Dornseif 2023-12-05 in huWaWi3 33.17.1
 * Copyright (c) 2023 HUDORA GmbH
 */

import { LadeChecker } from '@hudora/hd-react-components'
import { Typography } from '@mui/material'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Unstable_Grid2' // Grid version 2
import { useTitle } from 'fluentui-hooks'
import gql from 'graphql-tag'
import React from 'react'
import { useQuery } from 'urql'

import { HdCard } from './components/HdCard'

const MONITORING_AUFTRAEGE_QUERY = gql`
  query MonitoringAuftrag($status: String!) {
    auftragStats(status: $status) {
      count
      oldest
      youngest
      nrs
      status
      details {
        status
        detail {
          name
          designator
          anlieferdatum_von
          anlieferdatum_bis
          updated_at
          created_at
        }
      }
    }
  }
`
const MONITORING_KOMMIS_QUERY = gql`
  query MonitoringKommis {
    kommiStats {
      count
      oldest
      youngest
      letzte24h
      letzte24hcutoff
      nrs
      status
      details {
        status
        detail {
          name
          designator
          anlieferdatum_von
          anlieferdatum_bis
          updated_at
          created_at
        }
      }
    }
  }
`
const MONITORING_ENTWUERFE_QUERY = gql`
  query rechnungsentwurfStats {
    rechnungsentwurfStats {
      count
      oldest
      youngest
      letzte24h
      letzte24hcutoff
      nrs
      status
      details {
        status
        detail {
          name
          designator
          belegdatum
          deliveryId
          updated_at
          created_at
        }
      }
    }
  }
`
const MONITORING_BELEGE_QUERY = gql`
  query MonitoringBelege($art: String!) {
    belegStats(art: $art) {
      count
      oldest
      youngest
      letzte24h
      letzte24hcutoff
      nrs
      status
      details {
        status
        detail {
          name
          designator
          belegdatum
          deliveryId
          updated_at
          created_at
        }
      }
    }
  }
`
function Auftragsstatusanzeige({
  status,
  title,
  children,
}: {
  title: string
  status: string
  children?: React.ReactNode
}) {
  const [{ fetching, data, error }] = useQuery({
    query: MONITORING_AUFTRAEGE_QUERY,
    variables: { status },
  })

  return (
    <HdCard title={title}>
      <LadeChecker loading={fetching} error={error}>
        <dl>
          <dt>Anzahl</dt> <dd>{data?.auftragStats?.count}</dd>
          {data?.auftragStats?.count ? (
            <>
              <dt>Ältester</dt>
              <dd>{data?.auftragStats?.oldest} </dd>
              <dt>Jüngster</dt>
              <dd>{data?.auftragStats?.youngest}</dd>
            </>
          ) : null}
        </dl>
      </LadeChecker>
      {children}
    </HdCard>
  )
}
function KommiauftragStatusAnzeige() {
  const [{ fetching, data, error }] = useQuery({
    query: MONITORING_KOMMIS_QUERY,
  })

  return (
    <HdCard title={'Kommiaufträge'}>
      <LadeChecker loading={fetching} error={error}>
        <dl>
          <dt>Anzahl nicht erledigt</dt>
          <dd>{data?.kommiStats?.count}</dd>
          {data?.kommiStats?.count ? (
            <>
              <dt>Ältester</dt>
              <dd>{data?.kommiStats?.oldest} </dd>
              <dt>Jüngster</dt>
              <dd>{data?.kommiStats?.youngest}</dd>
            </>
          ) : null}
          <dt>In den letzten 24h erzeugt</dt>
          <dd>{data?.kommiStats?.letzte24h} </dd>
        </dl>
      </LadeChecker>
      <a href="https://huwawi3.hudora.de/ic/kommi">Kommiaufträge</a> werden aus Aufträgen in Status 400 und
      Zuteilung erzeugt.
      <br />
      In den letzten 24 Stunden erzeugt: TBD
    </HdCard>
  )
}
function EntwurfStatusAnzeige({ art, children }: { art: string; children?: React.ReactNode }) {
  const [{ fetching, data, error }] = useQuery({
    query: MONITORING_ENTWUERFE_QUERY,
    variables: { art },
  })
  return (
    <HdCard title="Rechnungsentwürfe">
      <LadeChecker loading={fetching} error={error}>
        <dl>
          <dt>In den letzten 24h erzeugt</dt>
          <dd>{data?.rechnungsentwurfStats?.letzte24h} </dd>
          {data?.belegStats?.count ? (
            <>
              <dt>Ältester</dt>
              <dd>{data?.rechnungsentwurfStats?.oldest} </dd>
              <dt>Jüngster</dt>
              <dd>{data?.rechnungsentwurfStats?.youngest}</dd>
            </>
          ) : null}
        </dl>
      </LadeChecker>
      {children}
    </HdCard>
  )
}
function BelegStatusAnzeige({ art, children }: { art: string; children?: React.ReactNode }) {
  const [{ fetching, data, error }] = useQuery({
    query: MONITORING_BELEGE_QUERY,
    variables: { art },
  })
  return (
    <HdCard title={art}>
      <LadeChecker loading={fetching} error={error}>
        <dl>
          <dt>In den letzten 24h erzeugt</dt>
          <dd>{data?.belegStats?.letzte24h} </dd>
          {data?.belegStats?.count ? (
            <>
              <dt>Ältester</dt>
              <dd>{data?.belegStats?.oldest} </dd>
              <dt>Jüngster</dt>
              <dd>{data?.belegStats?.youngest}</dd>
            </>
          ) : null}
        </dl>
      </LadeChecker>
      {children}
    </HdCard>
  )
}
export function WaWiMonitoringApp() {
  useTitle('huWaWi3 Monitoring')
  return (
    <div>
      <Box mt={2} mb={2}>
        <Typography variant="h4" component="h3">
          Aufträge (WaWi)
        </Typography>
        <Box sx={{ flexGrow: 1 }}>
          <Grid container spacing={2}>
            <Grid xs={4} md={2}>
              <Auftragsstatusanzeige status="0" title="010-099">
                Neu per EDI eingegangene <a href="/wawi/ic/a">Aufträge</a> werden in diesen Stati überprüft
                und mit Daten und Preisen angereichert. Die Verarbeitung kann manuell mit{' '}
                <code>huwawi-cli auftrag vorbereiten bis100</code> angestoßen werden.
              </Auftragsstatusanzeige>
            </Grid>
            <Grid xs={4} md={2}>
              <Auftragsstatusanzeige status="1" title="100-199">
                <a href="/wawi/ic/a">Aufträge</a> in diesem Status sind erfasst und können noch geändert
                werden. Ihre Weiterverarbeitung kann manuell mit{' '}
                <code>huwawi-cli auftrag freigeben bis300</code>{' '}
                angestossen werden.
              </Auftragsstatusanzeige>
            </Grid>
            <Grid xs={4} md={2}>
              <Auftragsstatusanzeige status="2" title="200-299">
                <a href="/wawi/ic/a">Aufträge</a> in diesem Status müssen genehmigt werde oder warten auf die
                Kreditlimitprüfung, die erst einige Tage vor der Zuteilung erfolgt.
              </Auftragsstatusanzeige>
            </Grid>
            <Grid xs={4} md={2}>
              <Auftragsstatusanzeige status="3" title="300-399">
                <a href="/wawi/ic/a">Aufträge</a> in Status 30x sind bereit zur Zuteilung werden automatisch
                in 400 überführt, wenn die Ware im Lager ist. Teillieferungen machend as ganze etwas
                komplizierter.
              </Auftragsstatusanzeige>
            </Grid>
            <Grid xs={4} md={2}>
              <Auftragsstatusanzeige status="4" title="400-499">
                <a href="/wawi/ic/a">Aufträge</a> in Status 40x sind zugeteilt und es gibt einen Komiauftrag
                dafür.
              </Auftragsstatusanzeige>
            </Grid>
            <Grid xs={4} md={2}>
              Erledigt in den letzten 24 h: TBD
            </Grid>
          </Grid>
        </Box>
      </Box>
      <Box mt={2} mb={2}>
        <Typography variant="h4" component="h3">
          Kommissionierung (Lager)
        </Typography>
        <Box sx={{ flexGrow: 1 }}>
          <Grid container spacing={2}>
            <Grid xs={4} md={2}>
              <KommiauftragStatusAnzeige />
            </Grid>
            <Grid xs={4} md={2}>
              <BelegStatusAnzeige art={'Kommissionierbeleg'}>
                <a href="/belege?belegart=Kommissionierbeleg">Kommibelege</a> werden aus dem Kommiaufträgen
                erzeugt und an das Lager übertragen. Unter jedem Kommibeleg kann man die Auslieferung an das
                jeweilige Lager (Sponeta, Mäuler, etc.) sehen.
              </BelegStatusAnzeige>
            </Grid>
            <Grid xs={4} md={2}>
              offene Zuteilungen TBD
            </Grid>
            <Grid xs={4} md={2}>
              <HdCard title={'Rückmeldedaten'}>
                Daten kommen per HTTP von Mäuler und werden von Rückmelde-Service von huLogi entgegengenommen.
                Der Rückmelde-Service ist mit huWaWi3-cli implementiert. Die empfangenen XML-Dateien liegen
                danach unter{' '}
                <a href="https://console.cloud.google.com/storage/browser/hudora-tmp-nearline/rueckmeldung/incoming">
                  in GCS
                </a>
                .
                <br />
                Im Anschluss sollte die Daten sofort automatisch weiter verarbeitet und an huWaWi2 und huWaWi3
                verteilt werden. Falls nicht, kann von Hand mit{' '}
                <code>huwawi-cli lager kommi rueckmeldeXmlGcs</code> verarbeitet werden.
                <br />
                Die Ergebnisse finden sich dann in GCS unter{' '}
                <a href="https://console.cloud.google.com/storage/browser/hudora-tmp-nearline/rueckmeldung/log/">
                  rueckmeldung/log/*/done
                </a>
                .
                <br />
                Zum Prüfen: erst in
                https://console.cloud.google.com/storage/browser/hudora-tmp-nearline/rueckmeldung/incoming/
                schauen.
                <hr />
                Rückmeldung von Sponeta: TBD.
              </HdCard>
            </Grid>
            <Grid xs={4} md={2}>
              <HdCard title={'Rückmeldeaufträge'}>
                Rückmeldeaufträge in huWaWi2 ändern den Auftragsstatus, löschen die{' '}
                <a href="/ic/zuteil">Zuteilung</a> und erzeugen entsprechende{' '}
                <a href="/ic/bestandsbuch">Bestandsbucheinträge</a>.
                <br />
                In den letzten 24 Stunden erzeugt: TBD
                <br />
                Letzter TBD
                <br />
                nicht erledigte Aufträge: TBD
              </HdCard>
            </Grid>
            <Grid xs={4} md={2}>
              <BelegStatusAnzeige art="Rückmeldung">
                <a href="/belege?belegart=Rückmeldung">Rückmeldebelege</a> in huWaWi3 stossen{' '}
                <a href="/belege?belegart=Lieferschein">Lieferschein-</a> und Rechnungsentwurfserzeugung an.
              </BelegStatusAnzeige>
            </Grid>
          </Grid>
        </Box>
      </Box>
      <Box mt={2} mb={2}>
        <Typography variant="h4" component="h3">
          Belege
        </Typography>

        <Box sx={{ flexGrow: 1 }}>
          <Grid container spacing={2}>
            <Grid xs={12} md={2}>
              <BelegStatusAnzeige art={'Bestellbestätigung'}>
                <a href="/belege?belegart=Bestellbest%C3%A4tigung">Bestellbestätigungen</a> werden genutzt, um
                ORDRSP zu triggern und damit zB Tchibo oder Shopware über unsere Auftragsnummern zu
                informieren.
                https://console.cloud.google.com/storage/browser/hudora-tmp-nearline/belege/Bestellbestätigung?pageState=(%22StorageObjectListTable%22:(%22f%22:%22%255B%255D%22))&project=huwawisql
              </BelegStatusAnzeige>
            </Grid>
            <Grid xs={12} md={1}>
              <HdCard title="Kommissionierbeleg">
                <a href="/belege?belegart=Kommissionierbeleg">Kommibelege</a> sind elementar wichtig, um das
                Lager zu informieren, was es tun soll. Es handelt sich um INSDES Nachrichten in der EDI-Denke.
              </HdCard>
            </Grid>
            <Grid xs={12} md={2}>
              {' '}
              <BelegStatusAnzeige art={'Vorablieferschein'}>
                Der <a href="/belege?belegart=Vorablieferschein">Vorablieferschein</a> ist ein Kommibeleg mit
                einer anderen Überschrift. Das Lager liebt sowas, weil sie nicht auf den richtigen
                Lieferschein warten müssen, bevor er das Paket zukleben kann. Er wird vor allem als PDF
                übertragen.
              </BelegStatusAnzeige>
            </Grid>
            <Grid xs={12} md={1}>
              <BelegStatusAnzeige art="Rückmeldung">
                <a href="/belege?belegart=Rückmeldung">Rückmeldebelege</a> sind im Grunde DESADV vom Lager an
                uns. Alles weitere oben.
                https://console.cloud.google.com/storage/browser/hudora-tmp-nearline/belege/Rückmeldung?pageState=(%22StorageObjectListTable%22:(%22f%22:%22%255B%255D%22))&project=huwawisql&prefix=&forceOnObjectsSortingFiltering=false{' '}
              </BelegStatusAnzeige>
            </Grid>
            <Grid xs={12} md={2}>
              <BelegStatusAnzeige art="Lieferschein">
                https://console.cloud.google.com/storage/browser/hudora-tmp-nearline/belege/Lieferschein?pageState=(%22StorageObjectListTable%22:(%22f%22:%22%255B%255D%22))&project=huwawisql&prefix=&forceOnObjectsSortingFiltering=false
              </BelegStatusAnzeige>
            </Grid>
            <Grid xs={12} md={2}>
              <EntwurfStatusAnzeige>
                Zu jedem Lieferschein gibt es einen Rechnungsentwurf. Daraus wird dann entweder eine Rechnung
                oder eine Sammelrechnung erstellt.
              </EntwurfStatusAnzeige>
            </Grid>
            <Grid xs={12} md={1}>
              <BelegStatusAnzeige art="Rechnung">
                https://console.cloud.google.com/storage/browser/hudora-tmp-nearline/belege/Rechnung
                https://console.cloud.google.com/cloudtasks/queue/europe-west3/belegqRG/tasks?project=huwawi3
              </BelegStatusAnzeige>
            </Grid>
            <Grid xs={12} md={1}>
              <BelegStatusAnzeige art="Sammelrechnung">
                https://console.cloud.google.com/storage/browser/hudora-tmp-nearline/belege/Sammelrechnung
                Werden per Cronjob erzeugt
              </BelegStatusAnzeige>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </div>
  )
}
